import { Component, Input, Output, EventEmitter, OnInit } from '@angular/core';
import { ScreenType } from 'src/app/enums/ScreenType';
import {
  INoticeBoardPosts,
  PostStatus,
  ReportStatus,
  IPostReports,
} from 'src/app/models/notice-board';
import { UserRegDetails } from 'src/app/models/user';

@Component({
  selector: 'app-post-reports',
  templateUrl: './post-reports.component.html',
  styleUrl: './post-reports.component.scss',
})
export class PostReportsComponent implements OnInit {
  @Input() set orgPosts(val: INoticeBoardPosts[]) {
    this.posts = val;
    this.categorizePosts();
    this.applyTimeFilter();
  }
  @Input() users: UserRegDetails[] = [];
  @Input() screenInfo: ScreenType = ScreenType.Desktop;
  @Input() loggedIsUsernameProfile: string;
  @Input() currentUserId: string;
  @Output() isOpened = new EventEmitter<boolean>();
  @Output() postFlaggedpostAction = new EventEmitter<any>();
  @Output() postActionTaken = new EventEmitter<{
    post: INoticeBoardPosts;
    action: PostStatus;
  }>();
  selectedTimeFilter: string = 'all';
  activeSection: string = 'pending'; // 'pending', 'reports', 'flagged', 'analytics'
  posts: INoticeBoardPosts[] = [];
  // Computed arrays based on input posts
  pendingApprovalPosts: INoticeBoardPosts[] = [];
  reportedPosts: INoticeBoardPosts[] = [];
  flaggedPosts: INoticeBoardPosts[] = [];
  approvedPosts: INoticeBoardPosts[] = [];
  rejectedPosts: INoticeBoardPosts[] = [];

  // Sorting and Filtering for Pending Approval Section
  pendingSortBy: string = 'date';

  // Sorting and Filtering for Reports Section
  reportStatusFilter: string = 'all';
  reportSortBy: string = 'date';

  // Sorting for Flagged Section
  flaggedSortBy: string = 'date';

  constructor() {}

  ngOnInit(): void {
    this.categorizePosts();
    this.applyTimeFilter();
  }

  ngOnChanges(): void {
    if (this.posts) {
      this.categorizePosts();
      this.applyTimeFilter();
    }
  }

  // Categorize posts based on their status and properties
  categorizePosts(): void {
    this.pendingApprovalPosts = this.posts.filter(
      (post) => post.isPending === true
    );
    this.approvedPosts = this.posts.filter((post) => post.isApproved === true);
    this.rejectedPosts = this.posts.filter(
      (post) => post.isApproved === false && post.isPending === false
    );
    this.reportedPosts = this.posts.filter(
      (post) => post.postReports && post.postReports.length > 0
    );
    this.flaggedPosts = this.posts.filter((post) => post.flaggedPost === true);
  }

  // --- General Dashboard Functions ---

  getLastUpdatedTime(): string {
    const now = new Date();
    return now.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  getTotalPosts(): number {
    return this.posts.length;
  }

  getPostsGrowth(): string {
    const today = new Date();
    const todayStart = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate()
    );
    const todayPosts = this.posts.filter(
      (post) => new Date(post.postAddDate) >= todayStart
    ).length;
    return `+${todayPosts} today`;
  }

  getPendingGrowth(): string {
    const today = new Date();
    const todayStart = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate()
    );
    const todayPending = this.pendingApprovalPosts.filter(
      (post) => new Date(post.postAddDate) >= todayStart
    ).length;
    return `+${todayPending} new today`;
  }

  getTotalReports(): number {
    return this.reportedPosts.reduce(
      (sum, post) => sum + (post.postReports?.length || 0),
      0
    );
  }

  getReportsGrowth(): string {
    const today = new Date();
    const weekStart = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
    const weeklyReports = this.reportedPosts.filter((post) =>
      post.postReports?.some(
        (report) => new Date(report.reportDate) >= weekStart
      )
    ).length;
    return `+${weeklyReports} this week`;
  }

  getApprovedCount(): number {
    return this.approvedPosts.length;
  }
  getRejectedGrowth(): string {
    const today = new Date();
    const weekStart = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
    const weeklyApproved = this.rejectedPosts.filter(
      (post) =>
        post.approvedDateTime && new Date(post.approvedDateTime) >= weekStart
    ).length;
    return `+${weeklyApproved} this week`;
  }
  getApprovedGrowth(): string {
    const today = new Date();
    const weekStart = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
    const weeklyApproved = this.approvedPosts.filter(
      (post) =>
        post.approvedDateTime && new Date(post.approvedDateTime) >= weekStart
    ).length;
    return `+${weeklyApproved} this week`;
  }

  getFlaggedGrowth(): string {
    const today = new Date();
    const dayStart = new Date(today.getTime() - 24 * 60 * 60 * 1000);
    const recentFlagged = this.flaggedPosts.filter(
      (post) => new Date(post.postAddDate) >= dayStart
    ).length;
    return `+${recentFlagged} in last 24h`;
  }

  getActiveUsersCount(): number {
    const uniqueUsers = new Set(this.posts.map((post) => post.postUserId));
    return uniqueUsers.size;
  }

  getActiveUsersGrowth(): string {
    const today = new Date();
    const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
    const monthlyActiveUsers = new Set(
      this.posts
        .filter((post) => new Date(post.postAddDate) >= monthStart)
        .map((post) => post.postUserId)
    );
    return `${monthlyActiveUsers.size} active this month`;
  }

  applyTimeFilter(): void {
    let filteredPosts = [...this.posts];

    if (this.selectedTimeFilter !== 'all') {
      const now = new Date();
      let filterDate: Date;

      switch (this.selectedTimeFilter) {
        case 'today':
          filterDate = new Date(
            now.getFullYear(),
            now.getMonth(),
            now.getDate()
          );
          break;
        case 'week':
          filterDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          break;
        case 'month':
          filterDate = new Date(now.getFullYear(), now.getMonth(), 1);
          break;
        default:
          filterDate = new Date(0);
      }

      filteredPosts = this.posts.filter(
        (post) => new Date(post.postAddDate) >= filterDate
      );
    }

    // Update categorized arrays with filtered data
    this.pendingApprovalPosts = filteredPosts.filter(
      (post) => post.isPending === true
    );
    this.approvedPosts = filteredPosts.filter(
      (post) => post.isApproved === true
    );
    this.rejectedPosts = filteredPosts.filter(
      (post) => post.isApproved === false && post.isPending === false
    );
    this.reportedPosts = filteredPosts.filter(
      (post) => post.postReports && post.postReports.length > 0
    );
    this.flaggedPosts = filteredPosts.filter(
      (post) => post.flaggedPost === true
    );

    this.sortPendingPosts();
    this.filterReports();
    this.sortFlaggedPosts();
  }

  toggleSection(section: string): void {
    this.activeSection = section;
  }

  getButtonClass(section: string): string {
    const baseClass =
      'flex items-center gap-2 px-4 py-2 rounded-lg transition-colors duration-200 text-sm';
    if (this.activeSection === section) {
      return `${baseClass} bg-blue-600 text-white shadow-md`;
    } else {
      return `${baseClass} bg-gray-100 text-gray-700 hover:bg-gray-200`;
    }
  }

  // --- Pending Approval Section Functions ---

  getUserRole(userId: string): string {
    return this.users.find((u) => u.userId === userId)?.userRole || 'Guest';
  }

  formatDate(date: Date): string {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  }

  getTimeSincePost(date: Date): string {
    const now = new Date();
    const seconds = Math.floor(
      (now.getTime() - new Date(date).getTime()) / 1000
    );

    let interval = seconds / 31536000;
    if (interval > 1) {
      return Math.floor(interval) + ' years ago';
    }
    interval = seconds / 2592000;
    if (interval > 1) {
      return Math.floor(interval) + ' months ago';
    }
    interval = seconds / 86400;
    if (interval > 1) {
      return Math.floor(interval) + ' days ago';
    }
    interval = seconds / 3600;
    if (interval > 1) {
      return Math.floor(interval) + ' hours ago';
    }
    interval = seconds / 60;
    if (interval > 1) {
      return Math.floor(interval) + ' minutes ago';
    }
    return Math.floor(seconds) + ' seconds ago';
  }

  getPriorityClass(post: INoticeBoardPosts): string {
    // Determine priority based on post properties
    if (post.isIncident) return 'bg-red-100 text-red-800';
    if (post.postReports && post.postReports.length > 0)
      return 'bg-orange-100 text-orange-800';
    if (post.flaggedPost) return 'bg-yellow-100 text-yellow-800';
    return 'bg-green-100 text-green-800';
  }

  getPostPriority(post: INoticeBoardPosts): string {
    if (post.isIncident) return 'High';
    if (post.postReports && post.postReports.length > 0) return 'Medium';
    if (post.flaggedPost) return 'Medium';
    return 'Low';
  }

  getPostStatusClass(post: INoticeBoardPosts): string {
    if (post.isPending) return 'bg-yellow-100 text-yellow-800';
    if (post.isApproved) return 'bg-green-100 text-green-800';
    if (post.isApproved === false && !post.isPending)
      return 'bg-red-100 text-red-800';
    if (post.flaggedPost) return 'bg-orange-100 text-orange-800';
    return 'bg-gray-100 text-gray-800';
  }

  getPostStatusText(post: INoticeBoardPosts): string {
    if (post.isPending) return 'Pending';
    if (post.isApproved) return 'Approved';
    if (post.isApproved === false) return 'Rejected';
    if (post.flaggedPost) return 'Flagged';
    return 'Unknown';
  }

  getAttachmentCount(post: INoticeBoardPosts): string {
    return post.postAttachment ? '1 file' : 'None';
  }

  getEngagementScore(post: INoticeBoardPosts): string {
    const views = 0; // Not available in interface
    const comments = post.postComments?.length || 0;
    const likes = post.postLikes?.length || 0;
    const score = views + comments * 5 + likes * 2;
    return `${score} points`;
  }

  getWaitingTime(postDate: Date): string {
    const now = new Date();
    const diffMs = now.getTime() - new Date(postDate).getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

    if (diffHours > 0) {
      return `${diffHours}h ${diffMinutes}m`;
    } else {
      return `${diffMinutes}m`;
    }
  }
  showApprovalModal: boolean = false;
  selectedPostForModal: INoticeBoardPosts | null = null;
  // Modal properties for Report Modal
  showReportModal: boolean = false;
  selectedPostForReportModal: INoticeBoardPosts | null = null;

  openApprovalModal(post: INoticeBoardPosts): void {
    // Emit event or call service for detailed review
    // In real implementation, this would open a modal dialog
    this.selectedPostForModal = { ...post };
    this.showApprovalModal = true;
  }
  confirmApproval(): void {
    if (this.selectedPostForModal) {
      this.approvePost(this.selectedPostForModal);
      this.closeModal();
    }
  }

  /**
   * Handles the rejection action from within the modal.
   * Calls the `rejectPost` method and then closes the modal.
   */
  confirmRejection(): void {
    if (this.selectedPostForModal) {
      this.rejectPost(this.selectedPostForModal);
      this.closeModal();
    }
  }
  rejectPost(post: INoticeBoardPosts): void {
    const index = this.pendingApprovalPosts.findIndex(
      (p) => p.postId === post.postId
    );
    if (index !== -1) {
      // Update the status of the original post object in the list
      // this.pendingApprovalPosts[index].isApproved = false;
      // // Remove the post from the pending list after rejection
      // this.pendingApprovalPosts = this.pendingApprovalPosts.filter(
      //   (p) => p.postId !== post.postId
      // );
    }
    this.postActionTaken.emit({ post: post, action: PostStatus.REJECTED });
  }

  /**
   * Closes the approval modal and clears the selected post.
   */
  closeModal(): void {
    this.showApprovalModal = false;
    this.selectedPostForModal = null;
  }

  quickReject(post: INoticeBoardPosts): void {
    // Update post status
    post.isApproved = false;
    post.isPending = false;
    post.approvedDateTime = new Date();
    post.approvedByName = 'Current User'; // Should be from auth service

    // Recategorize posts
    this.categorizePosts();

    // Emit event or call service to update backend
  }
  approvePost(post: INoticeBoardPosts): void {
    const index = this.pendingApprovalPosts.findIndex(
      (p) => p.postId === post.postId
    );
    if (index !== -1) {
      // Update the status of the original post object in the list
      // this.pendingApprovalPosts[index].isApproved = true;
      // // Remove the post from the pending list after approval
      // this.pendingApprovalPosts = this.pendingApprovalPosts.filter(
      //   (p) => p.postId !== post.postId
      // );
    }
    this.postActionTaken.emit({ post: post, action: PostStatus.APPROVED });
  }
  // approvePost(post: INoticeBoardPosts): void {
  //   // Update post status
  //   post.isApproved = true;
  //   post.isPending = false;
  //   post.approvedDateTime = new Date();
  //   post.approvedByName = 'Current User'; // Should be from auth service

  //   // Recategorize posts
  //   this.categorizePosts();

  //   // Emit event or call service to update backend
  // }

  sortPendingPosts(): void {
    this.pendingApprovalPosts.sort((a, b) => {
      if (this.pendingSortBy === 'date') {
        return (
          new Date(b.postAddDate).getTime() - new Date(a.postAddDate).getTime()
        );
      } else if (this.pendingSortBy === 'priority') {
        const priorityA = this.getPostPriority(a);
        const priorityB = this.getPostPriority(b);
        const priorityOrder = { High: 3, Medium: 2, Low: 1 };
        return (
          priorityOrder[priorityB as keyof typeof priorityOrder] -
          priorityOrder[priorityA as keyof typeof priorityOrder]
        );
      } else if (this.pendingSortBy === 'author') {
        return a.postUsername.localeCompare(b.postUsername);
      }
      return 0;
    });
  }

  getPendingDetails(): string {
    const highPriorityPending = this.pendingApprovalPosts.filter(
      (p) => this.getPostPriority(p) === 'High'
    ).length;

    if (highPriorityPending > 0) {
      return `${highPriorityPending} high priority`;
    }

    if (this.pendingApprovalPosts.length > 0) {
      return `Latest submitted: ${this.getTimeSincePost(
        this.pendingApprovalPosts[0].postAddDate
      )}`;
    }

    return 'No pending posts';
  }

  // --- Reports & Reviews Section Functions ---

  getCriticalReportsCount(): number {
    return this.reportedPosts.filter((post) =>
      post.postReports?.some(
        (report) =>
          report.reportStatus === ReportStatus.PENDING &&
          (report.reportReason === 'Hate Speech' ||
            report.reportReason === 'Inappropriate Content')
      )
    ).length;
  }

  getPendingReportsCount(): number {
    return this.reportedPosts.filter((post) =>
      post.postReports?.some(
        (report) => report.reportStatus === ReportStatus.PENDING
      )
    ).length;
  }

  getResolvedTodayCount(): number {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return this.reportedPosts.filter((post) =>
      post.postReports?.some(
        (report) =>
          report.reportStatus === ReportStatus.RESOLVED &&
          new Date(report.reportDate).setHours(0, 0, 0, 0) === today.getTime()
      )
    ).length;
  }

  getAvgResponseTime(): string {
    const resolvedReports = this.reportedPosts.flatMap(
      (post) =>
        post.postReports?.filter(
          (report) =>
            report.reportStatus === ReportStatus.RESOLVED && report.reviewDate
        ) || []
    );

    if (resolvedReports.length === 0) return '0h 0m';

    const totalTime = resolvedReports.reduce((sum, report) => {
      const reportTime = new Date(report.reportDate).getTime();
      const reviewTime = new Date(report.reviewDate).getTime();
      return sum + (reviewTime - reportTime);
    }, 0);

    const avgTimeMs = totalTime / resolvedReports.length;
    const avgHours = Math.floor(avgTimeMs / (1000 * 60 * 60));
    const avgMinutes = Math.floor((avgTimeMs % (1000 * 60 * 60)) / (1000 * 60));

    return `${avgHours}h ${avgMinutes}m`;
  }

  getReportSeverityClass(post: INoticeBoardPosts): string {
    const reasons = post.postReports?.map((r) => r.reportReason) || [];
    if (
      reasons.includes('Hate Speech') ||
      reasons.includes('Inappropriate Content')
    ) {
      return 'bg-red-100 text-red-800';
    }
    if (reasons.includes('Spam') || reasons.includes('Misinformation')) {
      return 'bg-orange-100 text-orange-800';
    }
    return 'bg-yellow-100 text-yellow-800';
  }
  getReportSeverityClassForReport(report: IPostReports): string {
    const reasons = [...report.reportReason];
    if (
      reasons.includes('Hate Speech') ||
      reasons.includes('Inappropriate Content')
    ) {
      return 'bg-red-100 text-red-800';
    }
    if (reasons.includes('Spam') || reasons.includes('Misinformation')) {
      return 'bg-orange-100 text-orange-800';
    }
    return 'bg-yellow-100 text-yellow-800';
  }
  getPostReportOverallStatusClass(post: INoticeBoardPosts): string {
    const status = this.getPostReportOverallStatusText(post);
    switch (status) {
      case 'PENDING':
        return 'bg-yellow-100 text-yellow-800';
      case 'REVIEWED':
        return 'bg-blue-100 text-blue-800';
      case 'RESOLVED':
        return 'bg-green-100 text-green-800';
      case 'No Reports':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  }
  getPostReportOverallStatusText(post: INoticeBoardPosts): string {
    if (!post.postReports || post.postReports.length === 0) return 'No Reports';
    if (
      post.postReports.some(
        (r) => r.reportStatus.toLocaleLowerCase() === ReportStatus.PENDING
      )
    )
      return 'PENDING';
    if (
      post.postReports.some(
        (r) => r.reportStatus.toLocaleLowerCase() === ReportStatus.REVIEWED
      )
    )
      return 'REVIEWED';
    if (
      post.postReports.every(
        (r) =>
          r.reportStatus.toLocaleLowerCase() === ReportStatus.RESOLVED ||
          r.reportStatus.toLocaleLowerCase() === ReportStatus.DISMISSED
      )
    )
      return 'RESOLVED';
    return 'UNKNOWN';
  }

  getReportSeverity(post: INoticeBoardPosts): string {
    const reasons = post.postReports?.map((r) => r.reportReason) || [];
    if (
      reasons.includes('Hate Speech') ||
      reasons.includes('Inappropriate Content')
    ) {
      return 'Critical';
    }
    if (reasons.includes('Spam') || reasons.includes('Misinformation')) {
      return 'High';
    }
    return 'Medium';
  }
  removePost(post: INoticeBoardPosts): void {
    const targetPost = this.reportedPosts.find((p) => p.postId === post.postId);
    // if (targetPost !== -1) {
    //   // Update the status of the original post object in the list
    //   // this.pendingApprovalPosts[index].isApproved = false;
    //   // // Remove the post from the pending list after rejection
    //   // this.pendingApprovalPosts = this.pendingApprovalPosts.filter(
    //   //   (p) => p.postId !== post.postId
    //   // );
    // }
    this.postFlaggedpostAction.emit({
      post: post,
      action: ReportStatus.BANNED,
    }); //Removing the Post
    this.closeFlaggedPostModal();
    // if (targetPost) {
    //   targetPost.status = PostStatus.REJECTED; // Mark as rejected/removed
    //   targetPost.actionTakenOnPost = 'Removed by Admin';
    //   // Resolve all associated flags
    //   targetPost.postReports?.forEach(report => {
    //     report.reportStatus = ReportStatus.RESOLVED;
    //     report.actionTaken = 'Post Removed';
    //   });
    //   this.postActionTaken.emit({ post: targetPost, action: 'postRemoved' });
    //   // Remove from reportedPosts entirely, or based on your data retention policy
    //   this.reportedPosts = this.reportedPosts.filter(p => p.postId !== post.postId);
    //   this.filterReports(); // Re-filter to update counts/display
    //   this.sortFlaggedPosts(); // Re-sort/filter flagged posts
    //   this.closeFlaggedPostModal(); // Close the modal after action
    // }
  }
  ignoreFlags(post: INoticeBoardPosts): void {
    const targetPost = this.reportedPosts.find((p) => p.postId === post.postId);
    this.postFlaggedpostAction.emit();
    this.closeFlaggedPostModal();
  }
  clearAllFlags(post: INoticeBoardPosts): void {
    const targetPost = this.reportedPosts.find((p) => p.postId === post.postId);
    this.postFlaggedpostAction.emit({
      post: post,
      action: ReportStatus.DISMISSED,
    }); //Removing the Post
    this.closeFlaggedPostModal();
    // if (targetPost && targetPost.postReports) {
    //   targetPost.postReports.forEach(report => {
    //     if (report.reportStatus === ReportStatus.PENDING || report.reportStatus === ReportStatus.REVIEWED) {
    //       report.reportStatus = ReportStatus.RESOLVED;
    //       report.actionTaken = 'Flags Cleared by Admin';
    //     }
    //   });
    //   this.postActionTaken.emit({ post: targetPost, action: 'flagsCleared' });
    //   this.filterReports(); // Re-filter to update counts/display
    //   this.sortFlaggedPosts(); // Re-sort/filter flagged posts
    //   this.closeFlaggedPostModal(); // Close the modal after action
    // }
  }
  getReportSeverityForReport(postReports: IPostReports): string {
    const reasons = [...postReports.reportReason];
    if (
      reasons.includes('Hate Speech') ||
      reasons.includes('Inappropriate Content')
    ) {
      return 'Critical';
    }
    if (reasons.includes('Spam') || reasons.includes('Misinformation')) {
      return 'High';
    }
    return 'Medium';
  }
  getPostActionTaken(post: INoticeBoardPosts): string {
    const latestReport = post.postReports?.[0];
    return latestReport?.actionTaken || 'No action yet';
  }

  getReportStatusClass(status: string): string {
    switch (status) {
      case ReportStatus.PENDING:
        return 'bg-yellow-100 text-yellow-800';
      case ReportStatus.REVIEWED:
        return 'bg-blue-100 text-blue-800';
      case ReportStatus.RESOLVED:
        return 'bg-green-100 text-green-800';
      case ReportStatus.DISMISSED:
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  }

  getReportReasonClass(reason: string): string {
    switch (reason) {
      case 'Hate Speech':
        return 'bg-red-100 text-red-800';
      case 'Inappropriate Content':
        return 'bg-purple-100 text-purple-800';
      case 'Spam':
        return 'bg-pink-100 text-pink-800';
      case 'Misinformation':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  }

  getFilteredReportedPosts(): INoticeBoardPosts[] {
    let filteredPosts = [...this.reportedPosts];

    // Filter by report status
    if (this.reportStatusFilter !== 'all') {
      filteredPosts = filteredPosts.filter((post) =>
        post.postReports?.some(
          (report) =>
            report.reportStatus === (this.reportStatusFilter as ReportStatus)
        )
      );
    }

    // Sort reports
    if (this.reportSortBy === 'date') {
      filteredPosts.sort((a, b) => {
        const latestReportA = a.postReports?.length
          ? Math.max(
              ...a.postReports.map((r) => new Date(r.reportDate).getTime())
            )
          : 0;
        const latestReportB = b.postReports?.length
          ? Math.max(
              ...b.postReports.map((r) => new Date(r.reportDate).getTime())
            )
          : 0;
        return latestReportB - latestReportA;
      });
    } else if (this.reportSortBy === 'severity') {
      const severityOrder: { [key: string]: number } = {
        Critical: 4,
        High: 3,
        Medium: 2,
        Low: 1,
      };
      filteredPosts.sort((a, b) => {
        const severityA = this.getReportSeverity(a);
        const severityB = this.getReportSeverity(b);
        return severityOrder[severityB] - severityOrder[severityA];
      });
    } else if (this.reportSortBy === 'status') {
      const statusOrder: { [key: string]: number } = {
        PENDING: 3,
        REVIEWED: 2,
        RESOLVED: 1,
        DISMISSED: 0,
      };
      filteredPosts.sort((a, b) => {
        const statusA = this.getPostReportOverallStatusText(a);
        const statusB = this.getPostReportOverallStatusText(b);
        return statusOrder[statusA] - statusOrder[statusB];
      });
    }

    return filteredPosts;
  }

  filterReports(): void {}

  sortReports(): void {}

  resolveReport(post: INoticeBoardPosts, reportId: string): void {
    const report = post.postReports?.find((r) => r.reportId === reportId);
    if (report) {
      report.reportStatus = ReportStatus.RESOLVED;
      report.reviewDate = new Date().toISOString();
      report.reviewedByName = 'Current User'; // Should be from auth service
      report.actionTaken = 'Report resolved, post kept.';

      this.updatePostStatusBasedOnReports(post);
    }
  }

  dismissReport(post: INoticeBoardPosts, reportId: string): void {
    const report = post.postReports?.find((r) => r.reportId === reportId);
    if (report) {
      report.reportStatus = ReportStatus.DISMISSED;
      report.reviewDate = new Date().toISOString();
      report.reviewedByName = 'Current User'; // Should be from auth service
      report.actionTaken = 'Report dismissed, no action taken on post.';

      this.updatePostStatusBasedOnReports(post);
    }
  }

  viewReporterProfile(reporterId: string): void {
    // Emit event or navigate to user profile
  }

  getReportsDetails(): string {
    const pendingReports = this.getPendingReportsCount();
    const criticalReports = this.getCriticalReportsCount();

    if (criticalReports > 0) {
      return `${criticalReports} critical, ${pendingReports} pending`;
    } else if (pendingReports > 0) {
      return `${pendingReports} pending reviews`;
    }
    return 'All reviewed';
  }

  private updatePostStatusBasedOnReports(post: INoticeBoardPosts): void {
    const hasPendingReports = post.postReports?.some(
      (r) => r.reportStatus === ReportStatus.PENDING
    );
    if (!hasPendingReports && post.flaggedPost) {
      post.flaggedPost = false;
      this.categorizePosts();
    }
  }

  // --- Flagged Content Section Functions ---

  sortFlaggedPosts(): void {
    this.flaggedPosts.sort((a, b) => {
      if (this.flaggedSortBy === 'date') {
        return (
          new Date(b.postAddDate).getTime() - new Date(a.postAddDate).getTime()
        );
      } else if (this.flaggedSortBy === 'flags') {
        const flagsA = a.postReports?.length || 0;
        const flagsB = b.postReports?.length || 0;
        return flagsB - flagsA;
      } else if (this.flaggedSortBy === 'status') {
        return this.getPostStatusText(a).localeCompare(
          this.getPostStatusText(b)
        );
      }
      return 0;
    });
  }
  getLatestFlagDate(post: INoticeBoardPosts): string {
    if (!post.postReports || post.postReports.length === 0) {
      return 'N/A';
    }
    const latestReport = post.postReports.reduce((prev, current) => {
      return new Date(prev.reportDate).getTime() >
        new Date(current.reportDate).getTime()
        ? prev
        : current;
    });
    return this.formatDate(latestReport.reportDate);
  }
  showFlaggedPostModal: boolean = false;
  selectedFlaggedPostForModal: INoticeBoardPosts | null = null;
  openFlaggedPostModal(post: INoticeBoardPosts): void {
    this.selectedFlaggedPostForModal = { ...post }; // Shallow copy
    this.showFlaggedPostModal = true;
  }
  closeFlaggedPostModal(): void {
    this.showFlaggedPostModal = false;
    this.selectedFlaggedPostForModal = null;
  }
  getFilteredFlaggedPosts(): INoticeBoardPosts[] {
    let filteredPosts = this.reportedPosts.filter((post) =>
      post.postReports?.some(
        (report) =>
          report.reportStatus.toLowerCase() === ReportStatus.PENDING ||
          report.reportStatus.toLowerCase() === ReportStatus.REVIEWED
      )
    );

    // Sort flagged posts
    if (this.flaggedSortBy === 'date') {
      filteredPosts.sort((a, b) => {
        const latestFlagA = a.postReports?.length
          ? Math.max(
              ...a.postReports.map((r) => new Date(r.reportDate).getTime())
            )
          : 0;
        const latestFlagB = b.postReports?.length
          ? Math.max(
              ...b.postReports.map((r) => new Date(r.reportDate).getTime())
            )
          : 0;
        return latestFlagB - latestFlagA;
      });
    } else if (this.flaggedSortBy === 'flags') {
      filteredPosts.sort(
        (a, b) => (b.postReports?.length || 0) - (a.postReports?.length || 0)
      );
    } else if (this.flaggedSortBy === 'status') {
      const statusOrder: { [key: string]: number } = {
        PENDING: 3,
        REVIEWED: 2,
        APPROVED: 1,
        REJECTED: 0,
      };
      filteredPosts.sort((a, b) => {
        const statusA = this.getPostStatusText(a); // Using post approval status
        const statusB = this.getPostStatusText(b); // Using post approval status
        return statusOrder[statusA] - statusOrder[statusB];
      });
    }

    return filteredPosts;
  }

  takeActionOnFlaggedPost(post: INoticeBoardPosts, action: string): void {
    switch (action) {
      case 'review':
        // Open detailed review modal
        this.openApprovalModal(post);
        break;
      case 'remove':
        post.hidePost = true;
        post.isApproved = false;
        this.categorizePosts();
        break;
      case 'warn':
        // Add warning action (would typically involve user service)
        break;
      case 'clear_flags':
        post.flaggedPost = false;
        if (post.postReports) {
          post.postReports.forEach((report) => {
            report.reportStatus = ReportStatus.RESOLVED;
            report.reviewDate = new Date().toISOString();
          });
        }
        this.categorizePosts();
        break;
    }
  }

  getFlaggedDetails(): string {
    const highPriorityFlags = this.flaggedPosts.filter(
      (p) => this.getPostPriority(p) === 'High'
    ).length;

    if (highPriorityFlags > 0) {
      return `${highPriorityFlags} high urgency`;
    }

    const totalFlags = this.flaggedPosts.reduce(
      (sum, p) => sum + (p.postReports?.length || 0),
      0
    );
    return `Total flags: ${totalFlags}`;
  }

  // --- Analytics Section Functions ---

  getApprovalRate(): number {
    const totalSubmissions = this.posts.filter(
      (p) => p.isApproved !== undefined || p.isPending === true
    ).length;

    if (totalSubmissions === 0) return 0;
    return parseFloat(
      ((this.approvedPosts.length / totalSubmissions) * 100).toFixed(2)
    );
  }

  getRejectionRate(): number {
    const totalSubmissions = this.posts.filter(
      (p) => p.isApproved !== undefined || p.isPending === true
    ).length;

    if (totalSubmissions === 0) return 0;
    return parseFloat(
      ((this.rejectedPosts.length / totalSubmissions) * 100).toFixed(2)
    );
  }

  getAvgReviewTime(): string {
    const reviewedPosts = this.posts.filter(
      (p) => p.approvedDateTime && p.postAddDate
    );

    if (reviewedPosts.length === 0) return '0 hours';

    const totalTime = reviewedPosts.reduce((sum, post) => {
      const submitTime = new Date(post.postAddDate).getTime();
      const reviewTime = new Date(post.approvedDateTime!).getTime();
      return sum + (reviewTime - submitTime);
    }, 0);

    const avgTimeMs = totalTime / reviewedPosts.length;
    const avgHours = Math.floor(avgTimeMs / (1000 * 60 * 60));
    const avgMinutes = Math.floor((avgTimeMs % (1000 * 60 * 60)) / (1000 * 60));

    return `${avgHours}.${Math.floor(avgMinutes / 6)} hours`;
  }

  // TrackBy functions for NgFor performance
  trackByPostId(index: number, post: INoticeBoardPosts): string {
    return post.postId;
  }

  trackByReportId(index: number, report: IPostReports): string {
    return report.reportId;
  }
  openReportModal(post: INoticeBoardPosts): void {
    this.selectedPostForReportModal = { ...post }; // Shallow copy
    this.showReportModal = true;
  }
  closeReportModal(): void {
    this.showReportModal = false;
    this.selectedPostForReportModal = null;
  }
  selectedFlags: string[] = [];
  reviewNotes: string = '';
  ReportStatus = ReportStatus;
  PostStatus = PostStatus;
  selectAllFlags(): void {
    this.selectedFlags = this.selectedFlaggedPostForModal.postReports.map(
      (report) => report.reportId
    );
  }

  deselectAllFlags(): void {
    this.selectedFlags = [];
  }
  toggleAllFlags(event: any): void {
    if (event.target.checked) {
      this.selectAllFlags();
    } else {
      this.deselectAllFlags();
    }
  }
  isReportSelected(reportId: string): boolean {
    return this.selectedFlags.includes(reportId);
  }
  toggleReportSelection(reportId: string, event: any): void {
    if (event.target.checked) {
      if (!this.selectedFlags.includes(reportId)) {
        this.selectedFlags.push(reportId);
      }
    } else {
      this.selectedFlags = this.selectedFlags.filter((id) => id !== reportId);
    }
  }

  areAllFlagsSelected(): boolean {
    return (
      this.selectedFlaggedPostForModal.postReports?.length > 0 &&
      this.selectedFlags.length ===
        this.selectedFlaggedPostForModal.postReports.length
    );
  }

  isSomeFlagsSelected(): boolean {
    return (
      this.selectedFlags.length > 0 &&
      this.selectedFlags.length <
        this.selectedFlaggedPostForModal.postReports?.length
    );
  }
}
// ____________________________________________________________
// ____________________________________________________________

// ____________________________________________________________
// ____________________________________________________________

// ____________________________________________________________
// ____________________________________________________________

// ____________________________________________________________
// ____________________________________________________________

//   @Input() posts: INoticeBoardPosts[] = [];
//   @Input() screenInfo: ScreenType = ScreenType.Desktop;
//   @Input() loggedIsUsernameProfile: string;
//   @Input() currentUserId: string;
//   @Output() isOpened = new EventEmitter<boolean>();
//   PostStatus = PostStatus;
//   ReportStatus = ReportStatus;
//   // Active section tracking
//   activeSection: 'pending' | 'reports' | 'flagged' | null = null;
//   selectedPost: INoticeBoardPosts | null = null;
//   selectedReport: IPostReports | null = null;

//   // Modal states
//   showApprovalModal = false;
//   showReportModal = false;
//   showReportDetailsModal = false;
//   // Form data
//   approvalAction: 'approve' | 'reject' | null = null;
//   approvalNotes = '';
//   reportReviewNotes = '';
//   reportAction = '';
//   constructor() {}

//   ngOnInit(): void {
//     // Initialize component
//     this.initializePendingStatus();
//   }

//   // Initialize pending status for posts that don't have it set
//   private initializePendingStatus(): void {
//     this.posts.forEach((post) => {
//       if (post.isPending === undefined) {
//         // Set isPending based on isApproved status
//         post.isPending =
//           post.isApproved === undefined || post.isApproved === null;
//       }
//     });
//   }

//   // Computed properties
//   get pendingApprovalPosts(): INoticeBoardPosts[] {
//     // Use isPending first, fallback to isApproved logic
//     return this.posts.filter((post) => {
//       if (post.isPending !== undefined) {
//         return post.isPending === true;
//       }
//       // Fallback for backward compatibility
//       return post.isApproved === false || post.isApproved === undefined;
//     });
//   }

//   get reportedPosts(): INoticeBoardPosts[] {
//     return this.posts.filter(
//       (post) => post.postReports && post.postReports.length > 0
//     );
//   }

//   get flaggedPosts(): INoticeBoardPosts[] {
//     return this.posts.filter((post) => post.flaggedPost === true);
//   }

//   get pendingReports(): IPostReports[] {
//     const reports: IPostReports[] = [];
//     this.posts.forEach((post) => {
//       if (post.postReports) {
//         reports.push(
//           ...post.postReports.filter(
//             (report) => report.reportStatus === ReportStatus.PENDING
//           )
//         );
//       }
//     });
//     return reports;
//   }

//   // Section management
//   toggleSection(section: 'pending' | 'reports' | 'flagged'): void {
//     this.activeSection = this.activeSection === section ? null : section;
//     this.isOpened.emit(this.activeSection != null);
//   }

//   // Post approval actions
//   openApprovalModal(post: INoticeBoardPosts): void {
//     this.selectedPost = post;
//     this.showApprovalModal = true;
//     this.approvalAction = null;
//     this.approvalNotes = '';
//   }

//   closeApprovalModal(): void {
//     this.showApprovalModal = false;
//     this.selectedPost = null;
//     this.approvalAction = null;
//     this.approvalNotes = '';
//   }

//   approvePost(): void {
//     if (this.selectedPost) {
//       this.selectedPost.isApproved = true;
//       this.selectedPost.isPending = false; // Update pending status
//       this.selectedPost.approvedBy = 'current-user-id'; // Replace with actual user ID
//       this.selectedPost.approvedByName = 'Current User'; // Replace with actual user name
//       this.selectedPost.approvedDateTime = new Date();

//       // Call your API service here
//         'Post approved:',
//         this.selectedPost.postId,
//         this.approvalNotes
//       );
//       this.closeApprovalModal();
//     }
//   }

//   rejectPost(): void {
//     if (this.selectedPost) {
//       this.selectedPost.isApproved = false;
//       this.selectedPost.isPending = false; // Update pending status
//       this.selectedPost.hidePost = true;

//       // Call your API service here
//         'Post rejected:',
//         this.selectedPost.postId,
//         this.approvalNotes
//       );
//       this.closeApprovalModal();
//     }
//   }

//   // Report management
//   openReportModal(post: INoticeBoardPosts): void {
//     this.selectedPost = post;
//     this.showReportModal = true;
//   }

//   closeReportModal(): void {
//     this.showReportModal = false;
//     this.selectedPost = null;
//   }

//   openReportDetailsModal(report: IPostReports): void {
//     this.selectedReport = report;
//     this.showReportDetailsModal = true;
//     this.reportReviewNotes = report.reviewNotes || '';
//     this.reportAction = report.actionTaken || '';
//   }

//   closeReportDetailsModal(): void {
//     this.showReportDetailsModal = false;
//     this.selectedReport = null;
//     this.reportReviewNotes = '';
//     this.reportAction = '';
//   }

//   updateReportStatus(status: ReportStatus): void {
//     if (this.selectedReport) {
//       this.selectedReport.reportStatus = status;
//       this.selectedReport.reviewDate = new Date().toISOString();
//       this.selectedReport.reviewNotes = this.reportReviewNotes;
//       this.selectedReport.actionTaken = this.reportAction;
//       this.selectedReport.reviewedBy = 'current-user-id'; // Replace with actual user ID
//       this.selectedReport.reviewedByName = 'Current User'; // Replace with actual user name

//       // Call your API service here
//       this.closeReportDetailsModal();
//     }
//   }

//   // Flag management
//   togglePostFlag(post: INoticeBoardPosts): void {
//     post.flaggedPost = !post.flaggedPost;

//     // Call your API service here
//   }

//   // Utility methods
//   formatDate(date: Date | string): string {
//     return new Date(date).toLocaleDateString();
//   }

//   getReportStatusClass(status: string): string {
//     switch (status) {
//       case ReportStatus.PENDING:
//         return 'bg-yellow-100 text-yellow-800';
//       case ReportStatus.REVIEWED:
//         return 'bg-blue-100 text-blue-800';
//       case ReportStatus.RESOLVED:
//         return 'bg-green-100 text-green-800';
//       case ReportStatus.DISMISSED:
//         return 'bg-gray-100 text-gray-800';
//       default:
//         return 'bg-gray-100 text-gray-800';
//     }
//   }

//   getPostStatusClass(post: INoticeBoardPosts): string {
//     // Use isPending first, then fallback to isApproved
//     if (post.isPending === true) {
//       return 'bg-yellow-100 text-yellow-800';
//     }

//     if (post.isApproved === true) {
//       return 'bg-green-100 text-green-800';
//     } else if (post.isApproved === false) {
//       return 'bg-red-100 text-red-800';
//     } else {
//       return 'bg-yellow-100 text-yellow-800';
//     }
//   }

//   getPostStatusText(post: INoticeBoardPosts): string {
//     // Use isPending first, then fallback to isApproved
//     if (post.isPending === true) {
//       return 'Pending';
//     }

//     if (post.isApproved === true) {
//       return 'Approved';
//     } else if (post.isApproved === false) {
//       return 'Rejected';
//     } else {
//       return 'Pending';
//     }
//   }
// }
